import { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { GitBranch, Network, Users } from 'lucide-react';
import { useI18n } from '@/i18n';
import {
  Button,
  SearchInput,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
  StatusBadge,
  IconButton,
  Spinner,
  type StatusType,
} from '@/components/ui';
import { useHives, useDeleteHive } from '@/features/agents/hives/hooks/useHives';
import type { Hive } from '@/features/agents/hives/types';

// =============================================================================
// Helpers
// =============================================================================

const mapHiveStatus = (hive: Hive): StatusType => {
  if (hive.agent_count === 0) return 'draft';
  if (!hive.entry_agent_id) return 'draft';
  return 'active';
};

const getHiveStatusLabel = (hive: Hive, t: (key: string) => string): string => {
  if (hive.agent_count === 0) return t('admin.hives.status.empty');
  if (!hive.entry_agent_id) return t('admin.hives.status.noEntry');
  return t('admin.hives.status.ready');
};

// =============================================================================
// Hives List Page
// =============================================================================

export function HivesListPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');

  const { data, isLoading, error } = useHives();
  const deleteMutation = useDeleteHive();

  const hives: Hive[] = useMemo(() => {
    if (!data) return [];
    return Array.isArray(data) ? data : (data as { data?: Hive[] }).data ?? [];
  }, [data]);

  const filteredHives = useMemo(
    () =>
      hives.filter((hive) =>
        hive.name.toLowerCase().includes(search.toLowerCase()) ||
        hive.description?.toLowerCase().includes(search.toLowerCase())
      ),
    [hives, search]
  );

  const handleDelete = async (id: string) => {
    if (window.confirm(t('admin.hives.confirmDelete'))) {
      await deleteMutation.mutateAsync(id);
    }
  };

  // ---------------------------------------------------------------------------
  // Loading / Error states
  // ---------------------------------------------------------------------------

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-700 dark:text-red-300">
            {t('common.error')}: {error instanceof Error ? error.message : 'Failed to load hives'}
          </p>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="p-6" data-testid="hives-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {t('admin.hives.title')}
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            {t('admin.hives.subtitle')}
          </p>
        </div>
        <Button
          leftIcon="plus"
          onClick={() => navigate('/agents/hives/new')}
          data-testid="create-hive-button"
        >
          {t('admin.hives.newHive')}
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-4 mb-6">
        <div className="flex-1">
          <SearchInput
            placeholder={t('admin.hives.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onClear={() => setSearch('')}
          />
        </div>
      </div>

      {/* Hives Table */}
      <Table data-testid="hive-list">
        <TableHeader>
          <TableRow>
            <TableHead>{t('admin.hives.table.name')}</TableHead>
            <TableHead>{t('admin.hives.table.agents')}</TableHead>
            <TableHead>{t('admin.hives.table.entryAgent')}</TableHead>
            <TableHead>{t('admin.hives.table.status')}</TableHead>
            <TableHead className="w-16"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filteredHives.length === 0 ? (
            <TableEmpty
              colSpan={5}
              message={t('admin.hives.emptyState')}
              action={
                <Button
                  variant="outline"
                  leftIcon="plus"
                  onClick={() => navigate('/agents/hives/new')}
                >
                  {t('admin.hives.newHive')}
                </Button>
              }
            />
          ) : (
            filteredHives.map((hive) => (
              <TableRow key={hive.id}>
                {/* Name + description */}
                <TableCell>
                  <Link
                    to={`/agents/hives/${hive.id}`}
                    className="flex items-center gap-3 group"
                  >
                    <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900 flex items-center justify-center">
                      <Network className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                    </div>
                    <div>
                      <span className="font-medium text-gray-900 dark:text-gray-100 group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors">
                        {hive.name}
                      </span>
                      {hive.description && (
                        <p className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-xs">
                          {hive.description}
                        </p>
                      )}
                    </div>
                  </Link>
                </TableCell>

                {/* Agent count */}
                <TableCell>
                  <div className="flex items-center gap-1 text-gray-600 dark:text-gray-400">
                    <Users className="h-4 w-4" />
                    <span>{hive.agent_count}</span>
                  </div>
                </TableCell>

                {/* Entry agent */}
                <TableCell className="text-gray-600 dark:text-gray-400">
                  {hive.entry_agent_id ? (
                    <div className="flex items-center gap-1">
                      <GitBranch className="h-4 w-4" />
                      <span className="truncate max-w-[160px]">
                        {hive.entry_agent_name ?? t('admin.hives.entryAgent')}
                      </span>
                    </div>
                  ) : (
                    <span className="text-gray-400 italic">
                      {t('admin.hives.noEntryAgent')}
                    </span>
                  )}
                </TableCell>

                {/* Status */}
                <TableCell>
                  <StatusBadge
                    status={mapHiveStatus(hive)}
                    label={getHiveStatusLabel(hive, t)}
                  />
                </TableCell>

                {/* Actions */}
                <TableCell>
                  <div className="flex items-center gap-2">
                    <IconButton
                      icon="trash"
                      label={t('common.delete')}
                      onClick={() => handleDelete(hive.id)}
                    />
                    <IconButton icon="more" label={t('common.actions')} />
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}
